/** How the Everyone view lays its tiles out in the box it has: as big as they can be, a page at a time when they cannot. */

export type GridKind = "laptop" | "tablet" | "phone";

export interface GridFitInput {
  /** The room left for tiles, after the grid's padding and the stage controls. */
  width: number;
  height: number;
  /** Everyone on the grid, the host included. */
  people: number;
  gap: number;
  kind: GridKind;
}

export interface GridFit {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  /** Tiles beyond the host's that one page holds. */
  perPage: number;
  /** The host takes 2 by 2 cells, from seven people up. */
  hostBig: boolean;
}

// Why: below these a face is too small to read, so the grid pages instead of shrinking further.
const MIN_TILE: Record<GridKind, number> = { laptop: 180, tablet: 150, phone: 110 };
const BIG_HOST_FROM = 7;

/** The columns, rows and tile size for this many people in this box. */
export function gridFit({ width, height, people, gap, kind }: GridFitInput): GridFit {
  const shape = kind === "phone" ? 1 : 16 / 9;
  const hostBig = people >= BIG_HOST_FROM;
  const cells = hostBig ? people + 3 : people;
  const floorCols = hostBig ? 2 : 1;
  const floorRows = hostBig ? 2 : 1;

  let best = { cols: floorCols, rows: Math.max(floorRows, Math.ceil(cells / floorCols)), tileWidth: 0 };
  // Why two columns for two: a pair sits side by side as halves, never stacked, on a computer or tablet.
  const pair = people === 2 && kind !== "phone";
  for (let cols = pair ? 2 : floorCols; cols <= (pair ? 2 : Math.max(floorCols, cells)); cols += 1) {
    const rows = Math.max(floorRows, Math.ceil(cells / cols));
    const tileWidth = Math.min((width - gap * (cols - 1)) / cols, ((height - gap * (rows - 1)) / rows) * shape);
    if (tileWidth > best.tileWidth) best = { cols, rows, tileWidth };
  }
  const minWidth = MIN_TILE[kind];
  if (best.tileWidth >= minWidth) {
    return { cols: best.cols, rows: best.rows, tileWidth: best.tileWidth, tileHeight: best.tileWidth / shape, perPage: people - 1, hostBig };
  }

  const minHeight = minWidth / shape;
  const cols = Math.max(floorCols, Math.floor((width + gap) / (minWidth + gap)));
  const rows = Math.max(floorRows, Math.floor((height + gap) / (minHeight + gap)));
  const held = cols * rows - (hostBig ? 4 : 1);
  return {
    cols,
    rows,
    tileWidth: (width - gap * (cols - 1)) / cols,
    tileHeight: (height - gap * (rows - 1)) / rows,
    perPage: Math.max(1, held),
    hostBig,
  };
}

/**
 * A small room's short last row is centred by laying the grid out in half columns: every tile spans two, and the
 * first tile of that row starts so the row sits in the middle. Null when the row is full or the host is big.
 */
export function centredRow(fit: GridFit, people: number): { index: number; column: number } | null {
  if (fit.hostBig) return null;
  const index = (Math.ceil(people / fit.cols) - 1) * fit.cols;
  const held = people - index;
  if (held >= fit.cols) return null;
  return { index, column: fit.cols - held + 1 };
}
